'use strict'

const cloneDeep_ = require('lodash/cloneDeep')
const isEqual_ = require('lodash/isEqual')
const flatten_ = require('lodash/flatten')
const omit_ = require('lodash/omit')

const { isSameRecord } = require('../recordStore/records')

const {
  isNone,
  isCallBeforeSaveHooks,
  isCheckHasDraft,
  isAssertValidity,
  isSaveRecord
} = require('./saveStates')
const {
  getPaginationData,
  selectCurrentRecord,
  selectCurrentRecordIndex,
  selectDefaultDraft,
  selectDesiredRecordIndex,
  selectFieldsToUpdate,
  selectRefreshCurrentRecord,
  selectRefreshCurrentView,
  selectRevertChanges,
  selectSaveState,
  selectUpdateSource
} = require('./reducer')

const {
  assertValidityCmd,
  notifyIndexChangeCmd,
  notifyRecordSetLoadedCmd,
  updateCurrentViewCmd
} = require('../adapters/adapterApiExecutor')
const {
  callBeforeSaveHooksCmd,
  checkHasDraftCmd,
  getRecordByIndexCmd,
  revertChangesCmd,
  saveRecordCmd,
  setFieldsInCurrentRecordCmd
} = require('../records/executor')
const { fireEventCmd } = require('../helpers/fireEventExecutor')

const omitSystemFields = record =>
  omit_(record, ['_id', '_createdDate', '_updatedDate'])

const subscriber = recordStore => {
  const processIndexChanges = ({ from, to, hasChanged, hasChangedToMatch }) => {
    const toCurrentIndex = selectCurrentRecordIndex(to)
    const fromCurrentIndex = selectCurrentRecordIndex(from)
    const toDesiredIndex = selectDesiredRecordIndex(to)
    const toRefreshCurrentRecord = selectRefreshCurrentRecord(to)
    const fromRefreshCurrentRecord = selectRefreshCurrentRecord(from)
    const commandsToReturn = []

    if (
      hasChangedToMatch(
        selectDesiredRecordIndex,
        desiredRecordIndex =>
          desiredRecordIndex >= 0 && desiredRecordIndex !== toCurrentIndex
      ) ||
      (toRefreshCurrentRecord && !fromRefreshCurrentRecord)
    ) {
      recordStore().matchWith({
        Pending: () => {
          throw new Error('Record store service not ready yet')
        },
        Ready: ({ instance }) => {
          const totalCount = instance.getMatchingRecordCount()
          const realIndex = Math.max(
            Math.min(toDesiredIndex, totalCount - 1),
            0
          )

          if (fromCurrentIndex !== realIndex || toRefreshCurrentRecord) {
            commandsToReturn.push(getRecordByIndexCmd(realIndex))
          }
        }
      })
    }

    if (hasChanged(selectCurrentRecordIndex) && fromCurrentIndex >= 0) {
      commandsToReturn.push(notifyIndexChangeCmd())
      commandsToReturn.push(
        fireEventCmd('currentIndexChanged', selectCurrentRecordIndex(to))
      )
    }

    return commandsToReturn
  }

  const processRecordChanges = ({
    from,
    to,
    hasChangedToMatch,
    hasChangedToTrue
  }) => {
    const toCurrentIndex = selectCurrentRecordIndex(to)
    const toCurrentRecord = selectCurrentRecord(to)
    const fromCurrentRecord = selectCurrentRecord(from)
    const commandsToReturn = []

    if (
      isSameRecord(fromCurrentRecord, toCurrentRecord) &&
      !isEqual_(
        omitSystemFields(fromCurrentRecord),
        omitSystemFields(toCurrentRecord)
      )
    ) {
      commandsToReturn.push(
        fireEventCmd(
          'itemValuesChanged',
          cloneDeep_(fromCurrentRecord),
          cloneDeep_(toCurrentRecord)
        )
      )
    }

    const toFieldsToUpdate = selectFieldsToUpdate(to)
    if (
      hasChangedToMatch(
        selectFieldsToUpdate,
        fieldsToUpdate => fieldsToUpdate != null
      )
    ) {
      commandsToReturn.push(
        setFieldsInCurrentRecordCmd(
          toFieldsToUpdate,
          toCurrentIndex,
          selectUpdateSource(to)
        )
      )
    }

    if (hasChangedToTrue(selectRevertChanges)) {
      commandsToReturn.push(
        revertChangesCmd(toCurrentIndex, selectDefaultDraft(to))
      )
    }

    return commandsToReturn
  }

  const processViewChanges = ({ hasChanged, hasChangedToTrue }) => {
    const commandsToReturn = []

    if (hasChanged(getPaginationData)) {
      commandsToReturn.push(notifyRecordSetLoadedCmd())
    }

    if (hasChangedToTrue(selectRefreshCurrentView)) {
      commandsToReturn.push(updateCurrentViewCmd())
    }

    return commandsToReturn
  }

  const processSaveStates = ({ from, to, hasChangedToMatch }) => {
    const toCurrentIndex = selectCurrentRecordIndex(to)
    const toCurrentRecord = selectCurrentRecord(to)
    const fromCurrentRecord = selectCurrentRecord(from)

    if (hasChangedToMatch(selectSaveState, isCallBeforeSaveHooks)) {
      return callBeforeSaveHooksCmd()
    } else if (hasChangedToMatch(selectSaveState, isCheckHasDraft)) {
      return checkHasDraftCmd(toCurrentIndex)
    } else if (hasChangedToMatch(selectSaveState, isAssertValidity)) {
      return assertValidityCmd(toCurrentRecord)
    } else if (hasChangedToMatch(selectSaveState, isSaveRecord)) {
      return saveRecordCmd(toCurrentIndex)
    } else if (
      isSaveRecord(selectSaveState(from)) &&
      isNone(selectSaveState(to))
    ) {
      return fireEventCmd(
        'afterSave',
        cloneDeep_(fromCurrentRecord),
        cloneDeep_(toCurrentRecord)
      )
    }
  }

  return transition =>
    flatten_(
      [
        processIndexChanges,
        processRecordChanges,
        processViewChanges,
        processSaveStates
      ].map(fn => fn(transition))
    )
}

module.exports = subscriber



// WEBPACK FOOTER //
// ./records/subscriber.js